import { isOptimisticGhostUid } from "@/lib/mail/mailMutationHold";
import type { MailSnoozeEntry } from "@/lib/snooze/constants";
import { snoozeEntryKey } from "@/lib/snooze/constants";
import { isMessageBodyCached } from "@/lib/mail/messageBodyCacheStatus";
import type { MailMessageSummary } from "@/lib/types/mail";

export function normalizeSnoozeSubject(subject: string | null | undefined): string {
  const trimmed = (subject ?? "").trim().replace(/\s+/g, " ");
  return trimmed.toLowerCase();
}

function resolveHoldUid(entry: MailSnoozeEntry): number {
  const holdUid = entry.holdUid ?? 0;
  return holdUid > 0 ? holdUid : entry.uid;
}

export function resolveSnoozeEntryHeader(
  entry: MailSnoozeEntry,
  folderHeaders: MailMessageSummary[]
): MailMessageSummary | undefined {
  const headers = folderHeaders.filter((msg) => msg.uid > 0 && !isOptimisticGhostUid(msg.uid));
  const holdFolder = entry.holdFolder?.trim();
  const lookupUid = holdFolder ? resolveHoldUid(entry) : entry.uid;

  const byUid = headers.find((msg) => msg.uid === lookupUid);
  if (byUid) return byUid;

  if (!holdFolder) {
    return headers.find((msg) => msg.uid === entry.uid);
  }

  const needle = normalizeSnoozeSubject(entry.subject);
  if (needle === "") return undefined;

  return headers.find((msg) => normalizeSnoozeSubject(msg.subject) === needle);
}

export function mergeSnoozeEntryWithHeader(
  entry: MailSnoozeEntry,
  header?: MailMessageSummary | null,
  accountId?: number
): MailMessageSummary {
  const holdFolder = entry.holdFolder?.trim();
  const bodyUid = header?.uid ?? (holdFolder ? resolveHoldUid(entry) : entry.uid);
  const imapFolder = holdFolder || entry.folder;

  let bodyCached = header?.bodyCached;
  if (bodyCached == null && accountId != null) {
    bodyCached = isMessageBodyCached(accountId, imapFolder, bodyUid);
  }

  return {
    uid: entry.uid,
    subject: header?.subject ?? (entry.subject?.trim() || "(Sans objet)"),
    from: header?.from ?? "En attente",
    date: header?.date ?? entry.until,
    seen: header?.seen ?? false,
    flagged: header?.flagged ?? false,
    favorite: header?.favorite,
    colorLabel: header?.colorLabel,
    size: header?.size ?? 0,
    hasAttachments: header?.hasAttachments,
    bodyCached,
    snoozeUntil: entry.until,
    imapFolder,
    imapBodyUid: holdFolder && bodyUid > 0 && bodyUid !== entry.uid ? bodyUid : undefined,
  };
}

export function headerLookupKey(folder: string, uid: number): string {
  return snoozeEntryKey(folder, uid);
}
